import React, { useState, useEffect } from 'react';
import useAuthStore from '../stores/authStore';

const SimpleCompetitorMatrix = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(14);
  const [startDate, setStartDate] = useState('');
  const { token } = useAuthStore();

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({ days: days.toString() });
      if (startDate) {
        params.append('start_date', startDate);
      }
      
      const response = await fetch(`/api/tenants/dashboard/competitor-matrix?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const matrixData = await response.json();
      console.log('📊 Matrix API Response:', matrixData);
      
      // Fetch demand data
      const demandResponse = await fetch(`/api/demand?location=sanur&days=${days}`);
      const demandData = demandResponse.ok ? await demandResponse.json() : { demand_scores: [] };
      console.log('📈 Demand API Response:', demandData);
      
      // Merge demand data into matrix data
      const enrichedMatrixData = (matrixData.matrix_data || []).map(row => {
        const demandScore = demandData.demand_scores?.find(d => d.date === row.date);
        return {
          ...row,
          demand_score: demandScore ? Math.round(demandScore.score) : null
        };
      });
      
      setData(enrichedMatrixData);
      
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, days, startDate]);

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === 0) {
      return 'No Data';
    }
    if (typeof price === 'number' && price > 0) {
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price);
    }
    return 'No Data';
  };

  const getAllHotels = () => {
    const hotels = new Set();
    data.forEach(row => {
      Object.keys(row.prices || {}).forEach(hotel => hotels.add(hotel));
    });
    const allHotels = Array.from(hotels);
    
    // Organize hotels: Sanur Lodge first, then others alphabetically
    const sanurLodge = allHotels.find(h => h === 'Sanur Lodge');
    const otherHotels = allHotels.filter(h => h !== 'Sanur Lodge').sort();
    
    return sanurLodge ? [sanurLodge, ...otherHotels] : otherHotels;
  };

  const exportToCSV = () => {
    const hotels = getAllHotels();
    const headers = ['Date', 'Market Demand', ...hotels];
    
    const csvContent = [
      headers.join(','),
      ...data.map(row => [
        row.formatted_date || row.date,
        row.demand_score ? `${row.demand_score}%` : 'N/A',
        ...hotels.map(hotel => {
          const price = row.prices?.[hotel];
          return formatPrice(price).replace(/,/g, '');
        })
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `competitor-matrix-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get price cell styling based on comparison
  const getPriceCellStyle = (price, rowData, hotelName) => {
    const isUserHotel = hotelName === 'Sanur Lodge';
    
    if (price === null || price === undefined || price === 0) {
      return isUserHotel 
        ? 'bg-gray-200 text-gray-600 font-semibold' 
        : 'bg-gray-100 text-gray-600';
    }
    
    // Get all valid prices for this date
    const validPrices = Object.values(rowData.prices || {}).filter(p => p && p > 0);
    if (validPrices.length === 0) {
      return isUserHotel ? 'bg-blue-100 text-blue-800 font-semibold' : 'bg-green-100 text-green-800';
    }
    
    const minPrice = Math.min(...validPrices);
    const maxPrice = Math.max(...validPrices);
    
    let bgColor = '';
    if (price === minPrice) {
      bgColor = 'bg-green-200 text-green-800'; // Lowest price - green
    } else if (price === maxPrice) {
      bgColor = 'bg-red-200 text-red-800'; // Highest price - red
    } else {
      bgColor = isUserHotel ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800';
    }
    
    return isUserHotel 
      ? `${bgColor} font-semibold` 
      : bgColor;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Simple Competitor Matrix</h2>
        <div className="text-center py-8">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Simple Competitor Matrix</h2>
        <div className="text-red-600 bg-red-50 p-4 rounded">
          Error: {error}
        </div>
      </div>
    );
  }

  const hotels = getAllHotels();

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Competitor Matrix
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Compare lowest room rates with competitors and market demand
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDays(7)}
              className={`px-3 py-1 text-sm rounded ${
                days === 7 
                  ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              7 Days
            </button>
            <button
              onClick={() => setDays(14)}
              className={`px-3 py-1 text-sm rounded ${
                days === 14 
                  ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              14 Days
            </button>
            <button
              onClick={() => setDays(30)}
              className={`px-3 py-1 text-sm rounded ${
                days === 30 
                  ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              30 Days
            </button>
            
            <div className="w-px h-6 bg-gray-300 mx-2"></div>
            
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2 py-1 text-sm border rounded"
              placeholder="Start Date"
            />
            
            <button
              onClick={() => setStartDate('')}
              className="px-2 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Clear
            </button>
            
            <div className="w-px h-6 bg-gray-300 mx-2"></div>
            
            <button
              onClick={exportToCSV}
              className="flex items-center gap-1 px-3 py-1 text-sm rounded bg-green-100 text-green-700 hover:bg-green-200"
              disabled={data.length === 0}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" x2="12" y1="15" y2="3"></line>
              </svg>
              CSV
            </button>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {data.length} days • {hotels.length} hotels • Data refreshed daily for next 90 days
          </div>
          
          {/* Legend */}
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-200 rounded"></div>
              <span>Lowest Price</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-200 rounded"></div>
              <span>Highest Price</span>
            </div>
          </div>
        </div>
      </div>

      {/* Simple Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Date
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                Market<br />Demand
              </th>
              {hotels.map((hotel, index) => (
                <th key={hotel} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  {hotel === 'Sanur Lodge' ? (
                    <div>
                      <div className="font-semibold text-blue-700">{hotel}</div>
                      <div className="text-xs text-blue-600">(Your Hotel)</div>
                    </div>
                  ) : (
                    hotel
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  {row.formatted_date || row.date}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                  <span className="text-gray-700">
                    {row.demand_score ? `${row.demand_score}%` : 'N/A'}
                  </span>
                </td>
                {hotels.map(hotel => {
                  const price = row.prices?.[hotel];
                  const cellStyle = getPriceCellStyle(price, row, hotel);
                  return (
                    <td key={hotel} className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      <span className={`px-2 py-1 rounded text-xs ${cellStyle}`}>
                        {formatPrice(price)}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Debug Info */}
      <div className="p-4 bg-gray-50 border-t text-xs text-gray-600">
        <details>
          <summary className="cursor-pointer font-medium">Debug Info (Click to expand)</summary>
          <pre className="mt-2 p-2 bg-white border rounded text-xs overflow-auto max-h-40">
            {JSON.stringify(data.slice(0, 3), null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
};

export default SimpleCompetitorMatrix;
